/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StatusChip from '../../../common-components/StatusChip';
import { Translation, TranslationKey } from '../../../helpers/types';
import { useAppDispatch } from '../../../helpers/hooks';
import { useWorkspaceSlice } from '../slice';

interface KeyRowProps {
  projectId: string;
  language: string;
  keyItem: TranslationKey;
  translation?: Translation;
  busy: boolean;
  locked?: boolean;
  onDelete: (keyItem: TranslationKey) => void;
}

export default function KeyRow({
  projectId,
  language,
  keyItem,
  translation,
  busy,
  locked = false,
  onDelete,
}: KeyRowProps) {
  const dispatch = useAppDispatch();
  const { actions } = useWorkspaceSlice();
  const [value, setValue] = useState(translation?.value ?? '');

  useEffect(() => {
    setValue(translation?.value ?? '');
  }, [translation?.value, translation?.version]);

  const dirty = value !== (translation?.value ?? '');

  const handleSave = () => {
    dispatch(
      actions.upsertTranslationRequest({
        projectId,
        keyId: keyItem.id,
        language,
        value,
      }),
    );
  };

  const handleAi = () => {
    dispatch(
      actions.aiTranslateKeyRequest({ projectId, keyId: keyItem.id, language }),
    );
  };

  const handleReview = (decision: 'approve' | 'reject') => {
    if (!translation) return;
    let note: string | undefined;
    if (decision === 'reject') {
      note = window.prompt('Reason for rejection') || undefined;
      if (!note) return;
    }
    dispatch(
      actions.reviewRequest({
        projectId,
        translationId: translation.id,
        keyId: keyItem.id,
        language,
        decision,
        note,
      }),
    );
  };

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top', maxWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
          {keyItem.key}
        </Typography>
        {keyItem.context && (
          <Typography variant="caption" color="text.secondary">
            {keyItem.context}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', maxWidth: 260 }}>
        <Typography variant="body2">{keyItem.sourceText}</Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', minWidth: 260 }}>
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Add a translation…"
          fullWidth
          multiline
          size="small"
          disabled={locked}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <StatusChip status={translation?.status} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }} align="right">
        {busy ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="Save">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!dirty || locked}
                  onClick={handleSave}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="AI translate">
              <span>
                <IconButton size="small" disabled={locked} onClick={handleAi}>
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Approve">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={!translation || locked}
                  onClick={() => handleReview('approve')}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reject">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!translation || locked}
                  onClick={() => handleReview('reject')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete key">
              <span>
                <IconButton
                  size="small"
                  disabled={locked}
                  onClick={() => onDelete(keyItem)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}
